import { useState, useEffect, useMemo, useCallback } from 'react';
import { RefrigerantCode, RefrigerantState, TemperatureUnit, PressureUnit, TemperatureValue, PressureValue, Recommendation } from './types';
import { REFRIGERANTS } from './fluid_data/refrigerant-mapping';
import { calculateSuperheatOrSubcooling, findSaturationTemperature } from './lib/refrigerant_calculations';
import { convertTempToK, convertPressureToPa, convertDeltaTempFromK, convertTempFromK, convertPressureFromPa } from './lib/unit-conversions';
import { Line } from 'react-chartjs-2';
import { ErrorChart } from './components/ErrorChart';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartArea
} from 'chart.js';
import './App.css';
import { loadBenchmarkData, getBenchmarkDataForRefrigerant, getErrorCategoryColor } from './lib/benchmark-data';
import { exampleData, hvacExamplesByCondition } from './lib/example-data';
import { refrigerantProperties } from './lib/refrigerant-properties';
import { hvacRefrigerantRanges, getHvacStatus, getHvacTroubleshootingInfo } from './lib/refrigerant-ranges';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface AnalysisResult {
  state: RefrigerantState;
  value: TemperatureValue;
  temperature: TemperatureValue;
  pressure: PressureValue;
  saturationTemperature: TemperatureValue;
  message: string;
}

// Gradient cache variables
// (Removed gradWidth, gradHeight, gradCache)
function getGradient(
  ctx: CanvasRenderingContext2D,
  chartArea: ChartArea | undefined,
  categories: string[],
  getColor: (cat: string) => string
): CanvasGradient {
  if (categories.length === 0) {
    const grad = ctx.createLinearGradient(0, 0, 100, 0);
    grad.addColorStop(0, '#2b2e75');
    grad.addColorStop(1, '#2b2e75');
    return grad;
  }
  const left = Number(chartArea?.left ?? 0);
  const right = Number(chartArea?.right ?? 100);
  const grad = ctx.createLinearGradient(left as number, 0, right as number, 0);
  if (categories.length === 1) {
    grad.addColorStop(0.5, getColor(categories[0]));
  } else {
    for (let i = 0; i < categories.length; i++) {
      const rel = i / (categories.length - 1);
      grad.addColorStop(rel, getColor(categories[i]));
    }
  }
  return grad;
}

function App() {
  const [selectedRefrigerant, setSelectedRefrigerant] = useState<RefrigerantCode>(RefrigerantCode.R22);
  
  // Suction line inputs (for superheat measurement)
  const [suctionTemperature, setSuctionTemperature] = useState<number>(15);
  const [suctionPressure, setSuctionPressure] = useState<number>(584);
  
  // Liquid line inputs (for subcooling measurement)
  const [liquidTemperature, setLiquidTemperature] = useState<number>(25);
  const [liquidPressure, setLiquidPressure] = useState<number>(1192);
  
  // Shared units for both measurements
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>(TemperatureUnit.C);
  const [pressureUnit, setPressureUnit] = useState<PressureUnit>(PressureUnit.KPAA);
  
  // Analysis results for both measurement points
  const [suctionAnalysisResult, setSuctionAnalysisResult] = useState<AnalysisResult | null>(null);
  const [liquidAnalysisResult, setLiquidAnalysisResult] = useState<AnalysisResult | null>(null);
  const [suctionError, setSuctionError] = useState<string | null>(null);
  const [liquidError, setLiquidError] = useState<string | null>(null);
  
  const [benchmarkData, setBenchmarkData] = useState<any[]>([]);
  const [suctionRecommendations, setSuctionRecommendations] = useState<Recommendation[]>([]);
  const [liquidRecommendations, setLiquidRecommendations] = useState<Recommendation[]>([]);
  const [advancedAnalysis, setAdvancedAnalysis] = useState<{
    diagnosis: string;
    efficiency: string;
    recommendations: Recommendation[];
    systemHealth: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  } | null>(null);

  // Function to populate inputs with example data for selected refrigerant
  const populateExampleData = useCallback((refrigerant: RefrigerantCode) => {
    const refrigerantExamples = exampleData[refrigerant as keyof typeof exampleData];
    if (!refrigerantExamples) return;

    // Find superheated example for suction line
    const superheatExample = refrigerantExamples.find(example => 
      example.type.includes('🔥') || example.type.toLowerCase().includes('superheat')
    );
    
    // Find subcooled example for liquid line
    const subcoolExample = refrigerantExamples.find(example => 
      example.type.includes('❄️') || example.type.toLowerCase().includes('subcool')
    );

    if (superheatExample) {
      // Convert pressure from kPa to selected unit
      const pressureInSelectedUnit = convertPressureFromPa(
        superheatExample.pressure * 1000, // Convert kPa to Pa
        pressureUnit
      );
      setSuctionTemperature(superheatExample.temperature);
      setSuctionPressure(pressureInSelectedUnit.pressure);
    }

    if (subcoolExample) {
      // Convert pressure from kPa to selected unit
      const pressureInSelectedUnit = convertPressureFromPa(
        subcoolExample.pressure * 1000, // Convert kPa to Pa
        pressureUnit
      );
      setLiquidTemperature(subcoolExample.temperature);
      setLiquidPressure(pressureInSelectedUnit.pressure);
    }
  }, [pressureUnit, setSuctionTemperature, setSuctionPressure, setLiquidTemperature, setLiquidPressure]);

  // Function to load example data by condition
  const loadExampleByCondition = useCallback((condition: string) => {
    const conditionData = hvacExamplesByCondition[condition];
    if (!conditionData) return;

    const refrigerantData = conditionData[selectedRefrigerant];
    if (!refrigerantData) return;

    // Find suction and liquid line examples
    const suctionExample = refrigerantData.find(example => example.type === 'Suction Line');
    const liquidExample = refrigerantData.find(example => example.type === 'Liquid Line');

    if (suctionExample) {
      // Convert pressure from kPa to selected unit
      const pressureInSelectedUnit = convertPressureFromPa(
        suctionExample.pressure * 1000, // Convert kPa to Pa
        pressureUnit
      );
      setSuctionTemperature(suctionExample.temperature);
      setSuctionPressure(pressureInSelectedUnit.pressure);
    }

    if (liquidExample) {
      // Convert pressure from kPa to selected unit
      const pressureInSelectedUnit = convertPressureFromPa(
        liquidExample.pressure * 1000, // Convert kPa to Pa
        pressureUnit
      );
      setLiquidTemperature(liquidExample.temperature);
      setLiquidPressure(pressureInSelectedUnit.pressure);
    }
  }, [selectedRefrigerant, pressureUnit, setSuctionTemperature, setSuctionPressure, setLiquidTemperature, setLiquidPressure]);

  // Advanced system analysis combining superheat and subcooling
  const performAdvancedAnalysis = useCallback(() => {
    if (!suctionAnalysisResult || !liquidAnalysisResult) {
      setAdvancedAnalysis(null);
      return;
    }

    // Only perform analysis if we have superheat and subcooling data
    if (suctionAnalysisResult.state !== RefrigerantState.Superheated || 
        liquidAnalysisResult.state !== RefrigerantState.Subcooled) {
      setAdvancedAnalysis({
        diagnosis: "System Analysis Incomplete",
        efficiency: "Cannot determine system efficiency without proper superheat and subcooling readings.",
        recommendations: [{
          text: "Ensure suction line measurements show superheated vapor and liquid line measurements show subcooled liquid for complete analysis.",
          severity: 'warning'
        }],
        systemHealth: 'fair'
      });
      return;
    }

    const superheat = suctionAnalysisResult.value.temperature;
    const subcooling = liquidAnalysisResult.value.temperature;
    
    // Get status for current readings using refrigerant-specific ranges
    const { superheatStatus, subcoolingStatus } = getHvacStatus(selectedRefrigerant, superheat, subcooling);

    // Get diagnosis using the comprehensive troubleshooting function
    const diagnosis = getHvacTroubleshootingInfo(superheatStatus, subcoolingStatus);
    
    // Determine system health and efficiency based on diagnosis
    let systemHealth: 'excellent' | 'good' | 'fair' | 'poor' | 'critical' = 'good';
    let efficiency = '';
    let recommendations: Recommendation[] = [];

    // Map diagnosis to system health and provide specific recommendations
    if (diagnosis === "System appears to be operating normally.") {
      systemHealth = 'excellent';
      efficiency = "System is operating at peak efficiency. Both superheat and subcooling are within optimal ranges, indicating proper refrigerant charge and good heat transfer.";
      recommendations = [];
    } else if (diagnosis === "Undercharged with refrigerant.") {
      systemHealth = 'poor';
      efficiency = "System efficiency is significantly reduced. The evaporator is not fully utilizing its heat absorption capacity, and the condenser is not providing adequate subcooling.";
      recommendations = [
        { text: "Add refrigerant to the system following manufacturer specifications", severity: 'critical' },
        { text: "Check for refrigerant leaks using leak detection equipment", severity: 'critical' },
        { text: "Verify proper system evacuation before adding refrigerant", severity: 'warning' }
      ];
    } else if (diagnosis === "Overcharged with refrigerant or restricted condenser airflow.") {
      systemHealth = 'poor';
      efficiency = "System efficiency is compromised. Excess refrigerant is backing up in the condenser, potentially causing liquid refrigerant to enter the compressor.";
      recommendations = [
        { text: "Remove excess refrigerant to bring subcooling within optimal range", severity: 'critical' },
        { text: "Check for liquid refrigerant returning to compressor (flooding)", severity: 'critical' },
        { text: "Verify expansion valve operation - may be stuck open", severity: 'warning' },
        { text: "Inspect compressor for liquid damage", severity: 'warning' }
      ];
    } else if (diagnosis === "Restriction in the liquid line (e.g., clogged filter drier).") {
      systemHealth = 'poor';
      efficiency = "System efficiency is reduced due to restricted refrigerant flow. The restriction causes refrigerant to back up in the condenser while starving the evaporator.";
      recommendations = [
        { text: "Check and replace filter if clogged", severity: 'critical' },
        { text: "Inspect liquid line for kinks, bends, or other restrictions", severity: 'critical' },
        { text: "Verify expansion valve operation - may be stuck closed", severity: 'warning' },
        { text: "Check for moisture or contaminants in the system", severity: 'warning' }
      ];
    } else if (diagnosis === "Inefficient compressor or a faulty/overfeeding metering device.") {
      systemHealth = 'critical';
      efficiency = "System efficiency is compromised due to poor compression or improper refrigerant metering. This can lead to inadequate heat transfer in both evaporator and condenser.";
      recommendations = [
        { text: "Test compressor efficiency and pumping capacity", severity: 'critical' },
        { text: "Check expansion valve operation and sizing", severity: 'critical' },
        { text: "Verify proper refrigerant charge after addressing mechanical issues", severity: 'warning' }
      ];
    } else if (diagnosis === "Condenser airflow issue (e.g., dirty coil, failing fan) or a slight overcharge.") {
      systemHealth = 'fair';
      efficiency = "System efficiency is reduced due to poor heat rejection at the condenser. This forces the system to work harder to achieve the same cooling effect.";
      recommendations = [
        { text: "Clean condenser coils to improve heat transfer", severity: 'warning' },
        { text: "Check condenser fan operation and airflow", severity: 'warning' },
        { text: "Verify proper condenser fan motor speed and direction", severity: 'warning' }
      ];
    } else if (diagnosis === "Evaporator airflow issue (e.g., dirty filter/coil) or a faulty metering device.") {
      systemHealth = 'fair';
      efficiency = "System efficiency is reduced due to poor heat absorption at the evaporator. This can lead to inadequate cooling and potential compressor damage.";
      recommendations = [
        { text: "Check evaporator fan operation and airflow", severity: 'warning' },
        { text: "Replace dirty air filters", severity: 'warning' },
        { text: "Clean evaporator coils if dirty or blocked", severity: 'warning' }
      ];
    } else if (diagnosis === "Metering device is underfeeding or the system has a slight undercharge.") {
      systemHealth = 'fair';
      efficiency = "System efficiency is reduced due to inadequate refrigerant flow through the metering device. This can cause poor evaporator performance.";
      recommendations = [
        { text: "Check expansion valve operation and sizing", severity: 'warning' },
        { text: "Verify proper refrigerant charge", severity: 'warning' },
        { text: "Inspect metering device for proper operation", severity: 'warning' }
      ];
    } else if (diagnosis === "Inefficient compressor or a slight undercharge.") {
      systemHealth = 'fair';
      efficiency = "System efficiency is compromised due to poor compression or slightly low refrigerant charge. This results in inadequate heat transfer.";
      recommendations = [
        { text: "Test compressor efficiency and pumping capacity", severity: 'warning' },
        { text: "Check refrigerant charge and add if necessary", severity: 'warning' },
        { text: "Verify proper system operation after adjustments", severity: 'warning' }
      ];
    } else {
      systemHealth = 'fair';
      efficiency = "System efficiency is not optimal. One or both parameters are outside ideal ranges.";
      recommendations = [
        { text: "Perform comprehensive system inspection", severity: 'warning' },
        { text: "Check refrigerant charge and system components", severity: 'warning' }
      ];
    }

    setAdvancedAnalysis({
      diagnosis,
      efficiency,
      recommendations,
      systemHealth
    });
  }, [suctionAnalysisResult, liquidAnalysisResult, selectedRefrigerant]);

  // Perform advanced analysis when both results are available
  useEffect(() => {
    performAdvancedAnalysis();
  }, [performAdvancedAnalysis]);

  // Load saved values from localStorage on component mount
  useEffect(() => {
    const savedRefrigerant = localStorage.getItem('selectedRefrigerant');
    const savedSuctionTemperature = localStorage.getItem('suctionTemperature');
    const savedSuctionPressure = localStorage.getItem('suctionPressure');
    const savedLiquidTemperature = localStorage.getItem('liquidTemperature');
    const savedLiquidPressure = localStorage.getItem('liquidPressure');
    const savedTemperatureUnit = localStorage.getItem('temperatureUnit');
    const savedPressureUnit = localStorage.getItem('pressureUnit');

    // Load units first
    if (savedTemperatureUnit && Object.values(TemperatureUnit).includes(savedTemperatureUnit as TemperatureUnit)) {
      setTemperatureUnit(savedTemperatureUnit as TemperatureUnit);
    }
    if (savedPressureUnit && Object.values(PressureUnit).includes(savedPressureUnit as PressureUnit)) {
      setPressureUnit(savedPressureUnit as PressureUnit);
    }

    // Load refrigerant
    let currentRefrigerant = selectedRefrigerant;
    if (savedRefrigerant && Object.values(RefrigerantCode).includes(savedRefrigerant as RefrigerantCode)) {
      currentRefrigerant = savedRefrigerant as RefrigerantCode;
      setSelectedRefrigerant(currentRefrigerant);
    }

    // Load saved input values
    if (savedSuctionTemperature) {
      const tempValue = parseFloat(savedSuctionTemperature);
      if (!isNaN(tempValue)) {
        setSuctionTemperature(tempValue);
      }
    }
    if (savedSuctionPressure) {
      const pressureValue = parseFloat(savedSuctionPressure);
      if (!isNaN(pressureValue)) {
        setSuctionPressure(pressureValue);
      }
    }
    if (savedLiquidTemperature) {
      const tempValue = parseFloat(savedLiquidTemperature);
      if (!isNaN(tempValue)) {
        setLiquidTemperature(tempValue);
      }
    }
    if (savedLiquidPressure) {
      const pressureValue = parseFloat(savedLiquidPressure);
      if (!isNaN(pressureValue)) {
        setLiquidPressure(pressureValue);
      }
    }

    // Note: Example data population is handled by the units useEffect below
  }, []);

  // Populate example data when refrigerant changes (but not on initial load)
  useEffect(() => {
    const savedRefrigerant = localStorage.getItem('selectedRefrigerant');
    // Only populate if this isn't the initial load
    if (savedRefrigerant && savedRefrigerant !== selectedRefrigerant) {
      populateExampleData(selectedRefrigerant);
    }
  }, [selectedRefrigerant, populateExampleData]);

  // Populate example data after units are loaded if no saved data exists
  useEffect(() => {
    const savedSuctionTemperature = localStorage.getItem('suctionTemperature');
    const savedSuctionPressure = localStorage.getItem('suctionPressure');
    const savedLiquidTemperature = localStorage.getItem('liquidTemperature');
    const savedLiquidPressure = localStorage.getItem('liquidPressure');
    
    const hasExistingData = savedSuctionTemperature || savedSuctionPressure || savedLiquidTemperature || savedLiquidPressure;
    
    if (!hasExistingData) {
      populateExampleData(selectedRefrigerant);
    }
  }, [selectedRefrigerant, populateExampleData]);

  // Save values to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('selectedRefrigerant', selectedRefrigerant);
    localStorage.setItem('suctionTemperature', suctionTemperature.toString());
    localStorage.setItem('suctionPressure', suctionPressure.toString());
    localStorage.setItem('liquidTemperature', liquidTemperature.toString());
    localStorage.setItem('liquidPressure', liquidPressure.toString());
    localStorage.setItem('temperatureUnit', temperatureUnit);
    localStorage.setItem('pressureUnit', pressureUnit);
  }, [selectedRefrigerant, suctionTemperature, suctionPressure, liquidTemperature, liquidPressure, temperatureUnit, pressureUnit]);

  // Analyze suction line state whenever inputs change
  useEffect(() => {
    try {
      setSuctionError(null);
      
      const fluidData = REFRIGERANTS.DATA[selectedRefrigerant];
      if (!fluidData) {
        throw new Error(`Fluid data not available for ${selectedRefrigerant}`);
      }

      const temperatureValue: TemperatureValue = {
        temperature: suctionTemperature,
        temperatureUnit
      };

      const pressureValue: PressureValue = {
        pressure: suctionPressure,
        pressureUnit
      };

      // Convert to Kelvin and Pascals for calculations
      const tempK = convertTempToK(temperatureValue);
      const pressurePa = convertPressureToPa(pressureValue);

      // Calculate superheat/subcooling
      const result = calculateSuperheatOrSubcooling(
        fluidData,
        tempK,
        pressurePa
      );

      // Calculate saturation temperature in user's unit
      const saturationTempK = tempK.temperature - result.value.temperature;
      const saturationTempUser = convertDeltaTempFromK(saturationTempK, temperatureUnit);

      setSuctionAnalysisResult({
        state: result.type,
        value: result.value, // Already in correct units
        temperature: temperatureValue,
        pressure: pressureValue,
        saturationTemperature: {
          temperature: saturationTempUser.temperature,
          temperatureUnit
        },
        message: getStateMessage(result.type, result.value.temperature, temperatureUnit)
      });

      // Generate recommendations based on temperature and pressure
      const tempInCelsius = convertTempFromK(convertTempToK({ temperature: suctionTemperature, temperatureUnit }).temperature, TemperatureUnit.C).temperature;
      const pressureInKpa = convertPressureFromPa(convertPressureToPa({ pressure: suctionPressure, pressureUnit }).pressure, PressureUnit.KPAA).pressure;

      const newRecommendations = generateRecommendations(tempInCelsius, pressureInKpa, selectedRefrigerant);
      setSuctionRecommendations(newRecommendations);

    } catch (error) {
      console.error('Error analyzing suction line state:', error);
      setSuctionError(error instanceof Error ? error.message : 'Unknown error occurred');
      setSuctionAnalysisResult(null);
    }
  }, [selectedRefrigerant, suctionTemperature, suctionPressure, temperatureUnit, pressureUnit]);

  // Analyze liquid line state whenever inputs change
  useEffect(() => {
    try {
      setLiquidError(null);
      
      const fluidData = REFRIGERANTS.DATA[selectedRefrigerant];
      if (!fluidData) {
        throw new Error(`Fluid data not available for ${selectedRefrigerant}`);
      }

      const temperatureValue: TemperatureValue = {
        temperature: liquidTemperature,
        temperatureUnit
      };

      const pressureValue: PressureValue = {
        pressure: liquidPressure,
        pressureUnit
      };

      // Convert to Kelvin and Pascals for calculations
      const tempK = convertTempToK(temperatureValue);
      const pressurePa = convertPressureToPa(pressureValue);

      // Calculate superheat/subcooling
      const result = calculateSuperheatOrSubcooling(
        fluidData,
        tempK,
        pressurePa
      );

      // Calculate saturation temperature in user's unit
      const saturationTempK = tempK.temperature - result.value.temperature;
      const saturationTempUser = convertDeltaTempFromK(saturationTempK, temperatureUnit);

      setLiquidAnalysisResult({
        state: result.type,
        value: result.value, // Already in correct units
        temperature: temperatureValue,
        pressure: pressureValue,
        saturationTemperature: {
          temperature: saturationTempUser.temperature,
          temperatureUnit
        },
        message: getStateMessage(result.type, result.value.temperature, temperatureUnit)
      });

      // Generate recommendations based on temperature and pressure
      const tempInCelsius = convertTempFromK(convertTempToK({ temperature: liquidTemperature, temperatureUnit }).temperature, TemperatureUnit.C).temperature;
      const pressureInKpa = convertPressureFromPa(convertPressureToPa({ pressure: liquidPressure, pressureUnit }).pressure, PressureUnit.KPAA).pressure;

      const newRecommendations = generateRecommendations(tempInCelsius, pressureInKpa, selectedRefrigerant);
      setLiquidRecommendations(newRecommendations);

    } catch (error) {
      console.error('Error analyzing liquid line state:', error);
      setLiquidError(error instanceof Error ? error.message : 'Unknown error occurred');
      setLiquidAnalysisResult(null);
    }
  }, [selectedRefrigerant, liquidTemperature, liquidPressure, temperatureUnit, pressureUnit]);

  // Load benchmark data for error bars
  useEffect(() => {
    const loadData = async () => {
      const data = await loadBenchmarkData();
      setBenchmarkData(data);
    };
    loadData();
  }, []);

  // Generate saturation curve data for the chart
  const chartData = useMemo(() => {
    if (!suctionAnalysisResult && !liquidAnalysisResult) return null;

    const fluidData = REFRIGERANTS.DATA[selectedRefrigerant];
    if (!fluidData) return null;

    // Get benchmark data for accuracy coloring
    const benchmarkDataForRefrigerant = getBenchmarkDataForRefrigerant(benchmarkData, selectedRefrigerant);
    const errorMap = new Map<number, { error: number; category: string }>();
    
    if (benchmarkDataForRefrigerant) {
      benchmarkDataForRefrigerant.errors.forEach(e => {
        // Round temperature to nearest degree for matching
        const roundedTemp = Math.round(e.temperature);
        errorMap.set(roundedTemp, { error: e.absoluteError, category: e.category });
      });
    }

    // Generate temperature range for saturation curve
    const tempRange = [];
    const pressureRange = [];
    const accuracyCategories = [];
    
    // Get critical and triple point temperatures
    const criticalTemp = fluidData.STATES.critical.T;
    const tripleTemp = fluidData.STATES.triple_liquid?.T || 200; // Default if not available
    
    // Generate points from triple point to critical point
    const numPoints = 30;
    for (let i = 0; i <= numPoints; i++) {
      const tempK = tripleTemp + (criticalTemp - tripleTemp) * (i / numPoints);
      const tempUser = convertTempFromK(tempK, temperatureUnit);
      
      try {
        // Use a reasonable pressure range for finding saturation temperature
        const minPressure = 1000; // Pa
        const maxPressure = fluidData.STATES.critical.p * 0.9; // Stay below critical
        
        // Binary search for pressure at this temperature
        let lowPressure = minPressure;
        let highPressure = maxPressure;
        let foundPressure = null;
        
        for (let iter = 0; iter < 10; iter++) {
          const midPressure = (lowPressure + highPressure) / 2;
          const testPressure = { pressure: midPressure, pressureUnit: PressureUnit.PAA };
          
          try {
            const satTemp = findSaturationTemperature(fluidData, testPressure);
            if (satTemp && Math.abs(satTemp.temperature - tempK) < 1.0) {
              foundPressure = midPressure;
              break;
            } else if (satTemp && satTemp.temperature < tempK) {
              lowPressure = midPressure;
            } else {
              highPressure = midPressure;
            }
          } catch (error) {
            highPressure = midPressure;
          }
        }
        
        if (foundPressure) {
          const pressureUser = convertPressureFromPa(foundPressure, pressureUnit);
          tempRange.push(tempUser.temperature);
          pressureRange.push(pressureUser.pressure);
          
          // Determine accuracy category for this temperature
          const roundedTemp = Math.round(tempUser.temperature);
          let errorInfo = errorMap.get(roundedTemp);
          
          // If no exact match, find the closest available error data
          if (!errorInfo && benchmarkDataForRefrigerant) {
            const availableTemps = Array.from(errorMap.keys()).sort((a, b) => Math.abs(a - roundedTemp) - Math.abs(b - roundedTemp));
            if (availableTemps.length > 0) {
              const closestTemp = availableTemps[0];
              errorInfo = errorMap.get(closestTemp);
            }
          }
          
          if (errorInfo) {
            accuracyCategories.push(errorInfo.category);
          } else {
            // Default to excellent if no benchmark data available
            accuracyCategories.push('excellent');
          }
        }
      } catch (error) {
        // Skip points that can't be calculated
        continue;
      }
    }

    // Current operating points
    const currentPoints = [];
    if (suctionAnalysisResult) {
      currentPoints.push({
        x: suctionAnalysisResult.temperature.temperature,
        y: suctionAnalysisResult.pressure.pressure,
        label: 'Suction Line'
      });
    }
    if (liquidAnalysisResult) {
      currentPoints.push({
        x: liquidAnalysisResult.temperature.temperature,
        y: liquidAnalysisResult.pressure.pressure,
        label: 'Liquid Line'
      });
    }

    return {
      saturationCurve: {
        temperatures: tempRange,
        pressures: pressureRange,
        accuracyCategories
      },
      currentPoints
    };
  }, [selectedRefrigerant, temperatureUnit, pressureUnit, suctionAnalysisResult, liquidAnalysisResult, benchmarkData]);

  // Single neutral line for the saturation curve
  const saturationCurveLineDataset = useMemo(() => {
    if (!chartData) return null;
    return {
      label: 'Saturation Curve',
      data: chartData.saturationCurve.temperatures.map((temp, index) => ({
        x: temp,
        y: chartData.saturationCurve.pressures[index]
      })),
      borderColor: function(context: any) {
        const chart = context.chart;
        const ctx = chart?.ctx;
        const chartArea = chart?.chartArea;
        if (!ctx || !chartArea) return '#2b2e75'; // fallback
        return getGradient(
          ctx,
          chartArea,
          chartData.saturationCurve.accuracyCategories,
          getErrorCategoryColor
        );
      },
      backgroundColor: function(context: any) {
        const chart = context.chart;
        const ctx = chart?.ctx;
        const chartArea = chart?.chartArea;
        if (!ctx || !chartArea) return '#2b2e75'; // fallback
        return getGradient(
          ctx,
          chartArea,
          chartData.saturationCurve.accuracyCategories,
          getErrorCategoryColor
        );
      },
      borderWidth: 4,
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 0,
      showLine: true,
      type: 'line' as const
    };
  }, [chartData]);

  // Input data point datasets
  const inputDataDatasets = useMemo(() => {
    if (!chartData?.currentPoints || chartData.currentPoints.length === 0) return [];
    
    return chartData.currentPoints.map((point, index) => ({
      label: point.label,
      data: [{ x: point.x, y: point.y }],
      backgroundColor: index === 0 ? '#ff6b6b' : '#4dd0e1', // Red for suction, blue for liquid
      borderColor: index === 0 ? '#ff6b6b' : '#4dd0e1',
      pointRadius: 8,
      pointHoverRadius: 10,
      showLine: false
    }));
  }, [chartData]);

  const chartDatasets = useMemo(() => {
    return [
      saturationCurveLineDataset,
      ...inputDataDatasets
    ].filter(Boolean) as any[]
  }, [saturationCurveLineDataset, inputDataDatasets]);

  // Gradient legend component
  const GradientLegend = () => {
    const categories = ['excellent', 'good', 'acceptable', 'poor', 'unacceptable'];
    const labels = ['Excellent', 'Good', 'Acceptable', 'Poor', 'Unacceptable'];
    
    return (
      <div style={{ 
        marginTop: '1rem', 
        textAlign: 'center',
        padding: '0.5rem',
        borderRadius: '4px'
      }}>
        <div style={{ 
          fontSize: '0.9rem', 
          fontWeight: 'bold', 
          marginBottom: '0.5rem',
          color: '#333'
        }}>
          Saturation Curve Accuracy
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          {categories.map((category, index) => (
            <div key={category} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.25rem',
              fontSize: '0.8rem'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: getErrorCategoryColor(category),
                borderRadius: '2px'
              }}></div>
              <span style={{ color: '#666' }}>{labels[index]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const generateRecommendations = (temperature: number, pressure: number, refrigerantCode: RefrigerantCode): Recommendation[] => {
    const recommendations: Recommendation[] = [];
    const refrigerant = refrigerantProperties[refrigerantCode];

    if (!refrigerant) {
      return recommendations;
    }

    // Temperature-based recommendations
    if (temperature > refrigerant.maxOperatingTempC) {
      recommendations.push({
        text: 'Reduce system load or improve cooling capacity',
        severity: 'critical'
      });
      recommendations.push({
        text: 'Check for refrigerant leaks or system blockages',
        severity: 'warning'
      });
      recommendations.push({
        text: 'Verify condenser fan operation and airflow',
        severity: 'warning'
      });
    } else if (temperature < refrigerant.minOperatingTempC) {
      recommendations.push({
        text: 'Increase system load or reduce cooling capacity',
        severity: 'critical'
      });
      recommendations.push({ 
        text: 'Check expansion valve operation', 
        severity: 'warning' 
      });
      recommendations.push({ 
        text: 'Verify evaporator fan operation', 
        severity: 'warning' 
      });
    }

    // Pressure-based recommendations
    if (pressure > refrigerant.maxOperatingPressureKpa) {
      recommendations.push({ 
        text: 'Check for overcharge condition', 
        severity: 'critical' 
      });
      recommendations.push({ 
        text: 'Verify condenser operation and airflow', 
        severity: 'warning' 
      });
      recommendations.push({
        text: 'Check for system blockages or restrictions',
        severity: 'warning'
      });
    } else if (pressure < refrigerant.minOperatingPressureKpa) {
      recommendations.push({ 
        text: 'Check for undercharge condition', 
        severity: 'critical' 
      });
      recommendations.push({ 
        text: 'Verify compressor operation', 
        severity: 'warning' 
      });
      recommendations.push({ 
        text: 'Check for refrigerant leaks', 
        severity: 'warning' 
      });
    }

    return recommendations;
  };

  const getStateMessage = (state: RefrigerantState, value: number, unit: TemperatureUnit): string => {
    const unitSymbol = unit === TemperatureUnit.C ? '°C' : unit === TemperatureUnit.F ? '°F' : 'K';
    switch (state) {
      case RefrigerantState.Superheated:
        return `Superheated vapor with ${value.toFixed(2)} ${unitSymbol} superheat`;
      case RefrigerantState.Subcooled:
        return `Subcooled liquid with ${value.toFixed(2)} ${unitSymbol} subcooling`;
      case RefrigerantState.Saturated:
        return 'Saturated (two-phase) condition';
      default:
        return 'Unknown state';
    }
  };

  const getStateColor = (state: RefrigerantState): string => {
    switch (state) {
      case RefrigerantState.Superheated:
        return '#eb9ba1';
      case RefrigerantState.Subcooled:
        return '#1f93c9';
      case RefrigerantState.Saturated:
        return '#2b2e75';
      default:
        return '#9293b8'; // dark-blue
    }
  };

  // Chart configuration
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: `Temperature-Pressure Chart for ${selectedRefrigerant}`,
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const tempUnit = temperatureUnit === TemperatureUnit.C ? '°C' : 
                           temperatureUnit === TemperatureUnit.F ? '°F' : 'K';
            return `Temperature: ${context[0].parsed.x.toFixed(2)} ${tempUnit}`;
          },
          label: (context) => {
            const pressureUnitLabel = pressureUnit === PressureUnit.KPAA ? 'kPa' :
                                    pressureUnit === PressureUnit.BARA ? 'bar' :
                                    pressureUnit === PressureUnit.PSIA ? 'psi' : 'Pa';
            return `Pressure: ${context.parsed.y.toFixed(2)} ${pressureUnitLabel}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: `Temperature (${temperatureUnit === TemperatureUnit.C ? '°C' : 
                               temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})`,
        },
      },
      y: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: `Pressure (${pressureUnit})`,
        },
      },
    },
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <div className="header-content">
            <img 
              src="/vision_air_logo.png" 
              alt="Vision Air Logo" 
              className="header-logo"
            />
            <div className="header-text">
              <h1>Refrigerant State Analyzer</h1>
              <p>Analyze the thermodynamic state of refrigerants</p>
            </div>
          </div>
        </header>
        
        <main className="main-content">
          {/* Top Controls Section */}
          <div className="top-controls">
            <div className="controls-row">
              <div className="control-group refrigerant-selection">
                <label htmlFor="refrigerant" className="control-label">
                  <span className="label-icon">🧪</span>
                  Refrigerant Type
                </label>
                <select
                  id="refrigerant"
                  value={selectedRefrigerant}
                  onChange={(e) => setSelectedRefrigerant(e.target.value as RefrigerantCode)}
                  className="control-select refrigerant-select"
                >
                  {Object.keys(REFRIGERANTS.DATA).map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>

              <div className="control-group units-selection">
                <label className="control-label">
                  <span className="label-icon">⚙️</span>
                  Measurement Units
                </label>
                <div className="units-container">
                  <div className="unit-control">
                    <select
                      id="temperature-unit"
                      value={temperatureUnit}
                      onChange={(e) => setTemperatureUnit(e.target.value as TemperatureUnit)}
                      className="control-select unit-select"
                    >
                      <option value={TemperatureUnit.C}>°C</option>
                      <option value={TemperatureUnit.F}>°F</option>
                      <option value={TemperatureUnit.K}>K</option>
                    </select>
                  </div>
                  <div className="unit-control">
                    <select
                      id="pressure-unit"
                      value={pressureUnit}
                      onChange={(e) => setPressureUnit(e.target.value as PressureUnit)}
                      className="control-select unit-select"
                    >
                      <option value={PressureUnit.KPAG}>kPa (gauge)</option>
                      <option value={PressureUnit.KPAA}>kPa (abs)</option>
                      <option value={PressureUnit.BARG}>bar (gauge)</option>
                      <option value={PressureUnit.BARA}>bar (abs)</option>
                      <option value={PressureUnit.PSIG}>psi (gauge)</option>
                      <option value={PressureUnit.PSIA}>psi (abs)</option>
                      <option value={PressureUnit.PAG}>Pa (gauge)</option>
                      <option value={PressureUnit.PAA}>Pa (abs)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="control-group actions">
                <label className="control-label">
                  <span className="label-icon">🎯</span>
                  Quick Examples
                </label>
                <div className="actions-grid">
                  <button 
                    type="button" 
                    onClick={() => loadExampleByCondition('Optimal')}
                    className="action-button optimal-button"
                    title="Load optimal system conditions"
                  >
                    <span className="button-icon">✅</span>
                    Optimal
                  </button>
                  <button 
                    type="button" 
                    onClick={() => loadExampleByCondition('Undercharged')}
                    className="action-button undercharged-button"
                    title="Load undercharged system conditions"
                  >
                    <span className="button-icon">📉</span>
                    Undercharged
                  </button>
                  <button 
                    type="button" 
                    onClick={() => loadExampleByCondition('Overcharged')}
                    className="action-button overcharged-button"
                    title="Load overcharged system conditions"
                  >
                    <span className="button-icon">📈</span>
                    Overcharged
                  </button>
                  <button 
                    type="button" 
                    onClick={() => loadExampleByCondition('Restriction')}
                    className="action-button restriction-button"
                    title="Load system with restriction"
                  >
                    <span className="button-icon">🚧</span>
                    Restriction
                  </button>
                  <button 
                    type="button" 
                    onClick={() => loadExampleByCondition('Inefficient Compressor')}
                    className="action-button compressor-button"
                    title="Load inefficient compressor conditions"
                  >
                    <span className="button-icon">⚙️</span>
                    Compressor
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main 3 Column Grid Layout */}
          <div className="main-grid">
            {/* Suction Line Input */}
            <div className="suction-line-input">
              <div className="input-section">
                <div style={{ padding: '20px', background: 'rgba(255, 107, 107, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 107, 107, 0.1)' }}>
                  <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px', fontSize: '1.2rem' }}>💨 Suction Line</h3>
                  
                  <div className="input-group">
                    <label htmlFor="suction-temperature">Temperature:</label>
                    <div className="input-with-unit">
                      <input
                        id="suction-temperature"
                        type="number"
                        value={suctionTemperature}
                        onChange={(e) => setSuctionTemperature(parseFloat(e.target.value) || 0)}
                        step="0.1"
                      />
                      <span style={{ padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '1rem', minWidth: '60px', textAlign: 'center' }}>
                        {temperatureUnit === TemperatureUnit.C ? '°C' : temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                      </span>
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="suction-pressure">Pressure:</label>
                    <div className="input-with-unit">
                      <input
                        id="suction-pressure"
                        type="number"
                        value={suctionPressure}
                        onChange={(e) => setSuctionPressure(parseFloat(e.target.value) || 0)}
                        step="0.1"
                      />
                      <span style={{ padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '1rem', minWidth: '80px', textAlign: 'center' }}>
                        {pressureUnit}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Suction Line Analysis */}
            <div className="">
                {suctionError && (
                  <div className="error-message">
                    <strong>Error:</strong> {suctionError}
                  </div>
                )}

                {suctionAnalysisResult && (
                  <div className="result-card" style={{ borderColor: getStateColor(suctionAnalysisResult.state) }}>
                    <div className="result-header">
                      <h3 style={{ color: getStateColor(suctionAnalysisResult.state) }}>
                      Suction Line Analysis - {suctionAnalysisResult.state}
                      </h3>
                    </div>
                    
                    <div className="result-details">
                      <div className="result-row">
                        <span className="label">Current Temperature:</span>
                        <span className="value">
                          {suctionAnalysisResult.temperature.temperature.toFixed(2)} 
                          {suctionAnalysisResult.temperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                           suctionAnalysisResult.temperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                        </span>
                      </div>
                      
                      <div className="result-row">
                        <span className="label">Current Pressure:</span>
                        <span className="value">
                          {suctionAnalysisResult.pressure.pressure.toFixed(0)} &nbsp;
                          {suctionAnalysisResult.pressure.pressureUnit}
                        </span>
                      </div>
                      
                      <div className="result-row">
                        <span className="label">Saturation Temperature:</span>
                        <span className="value">
                          {suctionAnalysisResult.saturationTemperature.temperature.toFixed(2)}
                          {suctionAnalysisResult.saturationTemperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                           suctionAnalysisResult.saturationTemperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                        </span>
                      </div>
                      
                      {(suctionAnalysisResult.state === RefrigerantState.Superheated || 
                        suctionAnalysisResult.state === RefrigerantState.Subcooled) && (
                        <div className="result-row">
                          <span className="label">
                            {suctionAnalysisResult.state === RefrigerantState.Superheated ? 'Superheat:' : 'Subcooling:'}
                          </span>
                          <span className="value">
                            {suctionAnalysisResult.value.temperature.toFixed(2)}
                            {suctionAnalysisResult.temperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                             suctionAnalysisResult.temperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                          </span>
                        </div>
                      )}
                    </div>
                  

                    {/* Suction Line Recommendations */}
                    {suctionRecommendations.length > 0 ? (
                      <div className="recommendations-section">
                        <div className="recommendations-list">
                          {suctionRecommendations.map((recommendation, index) => (
                            <div 
                              key={index} 
                              className={`recommendation-item recommendation-${recommendation.severity}`}
                            >
                              <span className="recommendation-icon">
                                {recommendation.severity === 'critical' ? '🚨' : 
                                 recommendation.severity === 'warning' ? '⚠️' : 'ℹ️'}
                              </span>
                              <span className="recommendation-text">{recommendation.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="recommendations-section">
                        <div className={`recommendation-item ${suctionAnalysisResult.state === RefrigerantState.Subcooled ? 'recommendation-warning' : 'recommendation-info'}`}>
                          <span className="recommendation-icon">
                            {suctionAnalysisResult.state === RefrigerantState.Subcooled ? '⚠️' : 'ℹ️'}
                          </span>
                          <span className="recommendation-text">
                            {suctionAnalysisResult.state === RefrigerantState.Subcooled ? 
                              "Unusual: Suction line shows subcooled liquid. Verify measurement location - suction line should typically show superheated vapor." :
                              suctionAnalysisResult.state === RefrigerantState.Saturated ?
                              "Suction line at saturation conditions. Monitor for proper superheat to protect compressor." :
                              "Operating within normal parameters for this measurement point."
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            {/* Liquid Line Input */}
            <div className="liquid-line-input">
              <div className="input-section">
                <div style={{ padding: '20px', background: 'rgba(77, 208, 225, 0.15)', borderRadius: '8px', border: '1px solid rgba(77, 208, 225, 0.1)' }}>
                  <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px', fontSize: '1.2rem' }}>💧 Liquid Line</h3>
                  
                  <div className="input-group">
                    <label htmlFor="liquid-temperature">Temperature:</label>
                    <div className="input-with-unit">
                      <input
                        id="liquid-temperature"
                        type="number"
                        value={liquidTemperature}
                        onChange={(e) => setLiquidTemperature(parseFloat(e.target.value) || 0)}
                        step="0.1"
                      />
                      <span style={{ padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '1rem', minWidth: '60px', textAlign: 'center' }}>
                        {temperatureUnit === TemperatureUnit.C ? '°C' : temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                      </span>
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="liquid-pressure">Pressure:</label>
                    <div className="input-with-unit">
                      <input
                        id="liquid-pressure"
                        type="number"
                        value={liquidPressure}
                        onChange={(e) => setLiquidPressure(parseFloat(e.target.value) || 0)}
                        step="0.1"
                      />
                      <span style={{ padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e9ecef', borderRadius: '6px', fontSize: '1rem', minWidth: '80px', textAlign: 'center' }}>
                        {pressureUnit}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liquid Line Analysis */}
            <div className="liquid-line-analysis">
                {liquidError && (
                  <div className="error-message">
                    <strong>Error:</strong> {liquidError}
                  </div>
                )}

                {liquidAnalysisResult && (
                  <div className="result-card" style={{ borderColor: getStateColor(liquidAnalysisResult.state) }}>
                    <div className="result-header">
                      <h3 style={{ color: getStateColor(liquidAnalysisResult.state) }}>
                      Liquid Line Analysis - {liquidAnalysisResult.state}
                      </h3>
                    </div>
                    
                    <div className="result-details">
                      <div className="result-row">
                        <span className="label">Current Temperature:</span>
                        <span className="value">
                          {liquidAnalysisResult.temperature.temperature.toFixed(2)} 
                          {liquidAnalysisResult.temperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                           liquidAnalysisResult.temperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                        </span>
                      </div>
                      
                      <div className="result-row">
                        <span className="label">Current Pressure:</span>
                        <span className="value">
                          {liquidAnalysisResult.pressure.pressure.toFixed(0)} &nbsp;
                          {liquidAnalysisResult.pressure.pressureUnit}
                        </span>
                      </div>
                      
                      <div className="result-row">
                        <span className="label">Saturation Temperature:</span>
                        <span className="value">
                          {liquidAnalysisResult.saturationTemperature.temperature.toFixed(2)}
                          {liquidAnalysisResult.saturationTemperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                           liquidAnalysisResult.saturationTemperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                        </span>
                      </div>
                      
                      {(liquidAnalysisResult.state === RefrigerantState.Superheated || 
                        liquidAnalysisResult.state === RefrigerantState.Subcooled) && (
                        <div className="result-row">
                          <span className="label">
                            {liquidAnalysisResult.state === RefrigerantState.Superheated ? 'Superheat:' : 'Subcooling:'}
                          </span>
                          <span className="value">
                            {liquidAnalysisResult.value.temperature.toFixed(2)}
                            {liquidAnalysisResult.temperature.temperatureUnit === TemperatureUnit.C ? '°C' : 
                             liquidAnalysisResult.temperature.temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
                          </span>
                        </div>
                      )}
                    </div>
                    

                    {/* Liquid Line Recommendations */}
                    {liquidRecommendations.length > 0 ? (
                      <div className="recommendations-section">
                        <div className="recommendations-list">
                          {liquidRecommendations.map((recommendation, index) => (
                            <div 
                              key={index} 
                              className={`recommendation-item recommendation-${recommendation.severity}`}
                            >
                              <span className="recommendation-icon">
                                {recommendation.severity === 'critical' ? '🚨' : 
                                 recommendation.severity === 'warning' ? '⚠️' : 'ℹ️'}
                              </span>
                              <span className="recommendation-text">{recommendation.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="recommendations-section">

                        <div className={`recommendation-item ${liquidAnalysisResult.state === RefrigerantState.Superheated ? 'recommendation-warning' : 'recommendation-info'}`}>
                          <span className="recommendation-icon">
                            {liquidAnalysisResult.state === RefrigerantState.Superheated ? '⚠️' : 'ℹ️'}
                          </span>
                          <span className="recommendation-text">
                            {liquidAnalysisResult.state === RefrigerantState.Superheated ? 
                              "Unusual: Liquid line shows superheated vapor, it should typically show subcooled liquid." :
                              liquidAnalysisResult.state === RefrigerantState.Saturated ?
                              "Liquid line at saturation conditions. Monitor for proper subcooling for system efficiency." :
                              "Operating within normal parameters for this measurement point."
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            {/* Advanced System Analysis */}
            <div className="main-column advanced-system-analysis">
              <div className="result-section">
                <h2>Advanced System Analysis</h2>
                
                {/* Advanced System Analysis */}
                {advancedAnalysis && (
                  <div className="result-card" style={{ 
                    borderColor: advancedAnalysis.systemHealth === 'excellent' ? '#28a745' :
                                advancedAnalysis.systemHealth === 'good' ? '#20c997' :
                                advancedAnalysis.systemHealth === 'fair' ? '#ffc107' :
                                advancedAnalysis.systemHealth === 'poor' ? '#fd7e14' : '#dc3545',
                    background: advancedAnalysis.systemHealth === 'excellent' ? 'rgba(40, 167, 69, 0.05)' :
                               advancedAnalysis.systemHealth === 'good' ? 'rgba(32, 201, 151, 0.05)' :
                               advancedAnalysis.systemHealth === 'fair' ? 'rgba(255, 193, 7, 0.05)' :
                               advancedAnalysis.systemHealth === 'poor' ? 'rgba(253, 126, 20, 0.05)' : 'rgba(220, 53, 69, 0.05)'
                  }}>
                    <div className="result-header">
                      <h3>
                        🔬 System Health: {advancedAnalysis.systemHealth.charAt(0).toUpperCase() + advancedAnalysis.systemHealth.slice(1)}
                      </h3>
                    </div>
                    
                    <div className="result-details">
                      <div className="result-row">
                        <span className="label">Diagnosis:</span>
                        <span className="value" style={{ 
                          color: advancedAnalysis.systemHealth === 'excellent' ? '#28a745' :
                                 advancedAnalysis.systemHealth === 'good' ? '#20c997' :
                                 advancedAnalysis.systemHealth === 'fair' ? '#ffc107' :
                                 advancedAnalysis.systemHealth === 'poor' ? '#fd7e14' : '#dc3545',
                          fontWeight: 'bold'
                        }}>
                          {advancedAnalysis.systemHealth === 'excellent' ? '🟢 Excellent' :
                           advancedAnalysis.systemHealth === 'good' ? '🟢 Good' :
                           advancedAnalysis.systemHealth === 'fair' ? '🟡 Fair' :
                           advancedAnalysis.systemHealth === 'poor' ? '🟠 Poor' : '🔴 Critical'}
                        </span>
                      </div>
                      
                      {suctionAnalysisResult?.state === RefrigerantState.Superheated && (
                        <div className="result-row">
                          <span className="label">
                            Superheat: 
                            {(() => {
                              const refrigerantData = hvacRefrigerantRanges[selectedRefrigerant];
                              if (refrigerantData) {
                                return ` (${refrigerantData.targetSuperheatMinC.toFixed(1)}°C - ${refrigerantData.targetSuperheatMaxC.toFixed(1)}°C optimal)`;
                              }
                              return ' (8.0°C - 15.0°C optimal)';
                            })()}
                          </span>
                          <span className="value">
                            {suctionAnalysisResult.value.temperature.toFixed(1)}°C 
                            {(() => {
                              const refrigerantData = hvacRefrigerantRanges[selectedRefrigerant];
                              const optimalRange = refrigerantData?.ranges.find(range => range.status === 'Optimal');
                              if (optimalRange) {
                                const minC = optimalRange.superheatRangeMinC;
                                const maxC = optimalRange.superheatRangeMaxC;
                                return suctionAnalysisResult.value.temperature >= minC && suctionAnalysisResult.value.temperature <= maxC ? ' ✅' : ' ⚠️';
                              }
                              return suctionAnalysisResult.value.temperature >= 8 && suctionAnalysisResult.value.temperature <= 15 ? ' ✅' : ' ⚠️';
                            })()}
                          </span>
                        </div>
                      )}
                      
                      {liquidAnalysisResult?.state === RefrigerantState.Subcooled && (
                        <div className="result-row">
                          <span className="label">
                            Subcooling: 
                            {(() => {
                              const refrigerantData = hvacRefrigerantRanges[selectedRefrigerant];
                              if (refrigerantData) {
                                return ` (${refrigerantData.targetSubcoolingMinC.toFixed(1)}°C - ${refrigerantData.targetSubcoolingMaxC.toFixed(1)}°C optimal)`;
                              }
                              return ' (5.0°C - 12.0°C optimal)';
                            })()}
                          </span>
                          <span className="value">
                            {liquidAnalysisResult.value.temperature.toFixed(1)}°C 
                            {(() => {
                              const refrigerantData = hvacRefrigerantRanges[selectedRefrigerant];
                              const optimalRange = refrigerantData?.ranges.find(range => range.status === 'Optimal');
                              if (optimalRange) {
                                const minC = optimalRange.subcoolingRangeMinC;
                                const maxC = optimalRange.subcoolingRangeMaxC;
                                return liquidAnalysisResult.value.temperature >= minC && liquidAnalysisResult.value.temperature <= maxC ? ' ✅' : ' ⚠️';
                              }
                              return liquidAnalysisResult.value.temperature >= 5 && liquidAnalysisResult.value.temperature <= 12 ? ' ✅' : ' ⚠️';
                            })()}
                          </span>
                        </div>
                      )}

                    </div>
                    
                    <div className="result-message">
                      <strong>Efficiency Analysis:</strong> {advancedAnalysis.efficiency}
                    </div>

                    {/* Advanced Analysis Recommendations - Only for non-excellent systems */}
                {advancedAnalysis && advancedAnalysis.systemHealth !== 'excellent' && advancedAnalysis.recommendations.length > 0 && (
                    <div className="recommendations-list">
                      {advancedAnalysis.recommendations
                        .filter(rec => rec.severity === 'critical' || rec.severity === 'warning')
                        .map((recommendation, index) => (
                        <div 
                          key={index} 
                          className={`recommendation-item recommendation-${recommendation.severity}`}
                        >
                          <span className="recommendation-icon">
                            {recommendation.severity === 'critical' ? '🚨' : '⚠️'}
                          </span>
                          <span className="recommendation-text">{recommendation.text}</span>
                        </div>
                      ))}
                    </div>
                )}
                  </div>
                )}

                

              </div>
            </div>
          </div>
          
          {/* Temperature-Pressure Chart */}
          {chartData && (
            <div className="chart-section">
              <h2>{`Temperature-Pressure Chart for ${selectedRefrigerant}`}</h2>
              <div className="chart-container">
                <Line
                  options={chartOptions}
                  data={{
                    labels: chartData.saturationCurve.temperatures,
                    datasets: chartDatasets
                  }}
                />
              </div>

              <GradientLegend />
            </div>
          )}
          
          {/* Calculation Accuracy Analysis Chart */}
          <ErrorChart 
            selectedRefrigerant={selectedRefrigerant} 
            temperatureUnit={temperatureUnit}
            pressureUnit={pressureUnit}
          />
        </main>
      </div>
    </div>
  );
}

export default App;